import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Payment } from './models';
import { baseApi } from '@centerpiece/components';

export const payment = baseApi
  .enhanceEndpoints({ addTagTypes: ['Payments', 'Payment'] })
  .injectEndpoints({
    endpoints: builder => ({
      getPayments: builder.query<Payment[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'payment',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Payment' as const, id })),
                'Payments',
              ]
            : ['Payments'],
      }),
      getPayment: builder.query<Payment, string>({
        query: id => ({
          url: `payment/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Payment', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createPayment: builder.mutation({
        query: request => ({
          url: 'payment',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Payments'],
      }),
      updatePayment: builder.mutation<Payment, Payment>({
        query: request => ({
          url: `payment/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Payment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            payment.util.updateQueryData('getPayment', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(payment.util.invalidateTags(['Payments']))
             */
            dispatch(payment.util.invalidateTags(['Payments']));
          }
        },
      }),
      deletePayment: builder.mutation<Payment, number>({
        query: id => ({
          url: `payment/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Payment' as const, arg }, 'Payments']
            : ['Payments'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetPaymentsQuery,
  useGetPaymentQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
  usePrefetch: usePaymentsPrefetch,
} = payment;
const selectPaymentsRoot = payment.endpoints.getPayments.select({});
export const selectPayments = createSelector(
  selectPaymentsRoot,
  payment => payment.data || []
);
